// Placement list filtering.
//
// Every filter is multi-select: users routinely want two or three dimensions
// at once, and picking one at a time meant one pass per size. "All" is an
// ordinary option that means "do not filter on this field", and it is
// mutually exclusive with everything else — ticking a real value unticks it,
// unticking the last real value brings it back.
//
// Values are arrays. They used to be bare strings, except start and end,
// which were already multi-select smuggled through as a comma-joined string
// — fine for ISO dates, but not something to extend to site names or serving
// types. Arrays also let the chips remove one value at a time.

import dayjs from "dayjs";

export const ALL = "All";

export const FILTER_KEYS = [
  "status",
  "site",
  "start",
  "end",
  "leadTime",
  "lastModified",
  "dimensions",
  "compatibility",
  "servingType"
];

export function createDefaultFilters() {
  return FILTER_KEYS.reduce((acc, key) => {
    acc[key] = [ALL];
    return acc;
  }, {});
}

/** Tolerates the old bare-string and comma-joined shapes. */
export function toFilterArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return value.split(",");
  return [ALL];
}

export function isAllSelected(value) {
  const list = toFilterArray(value);
  return list.length === 0 || list.includes(ALL);
}

/**
 * Reconcile a multi-select change against the All option.
 *
 * @param {Array} previous - the filter's value before the click
 * @param {Array} selected - the raw array the Select hands back
 */
export function nextFilterSelection(previous, selected) {
  const before = toFilterArray(previous);
  const after = Array.isArray(selected) ? selected : [selected];

  // All was just ticked, so it wins and clears the rest.
  if (after.includes(ALL) && !before.includes(ALL)) return [ALL];

  const withoutAll = after.filter((value) => value !== ALL);

  // Unticking the last real value falls back to All rather than leaving an
  // empty selection, which would read as "match nothing" and blank the table.
  return withoutAll.length ? withoutAll : [ALL];
}

/** Drop one value, falling back to All when it was the last one. */
export function removeFilterValue(previous, value) {
  const remaining = toFilterArray(previous).filter((v) => v !== value);
  return remaining.length ? remaining : [ALL];
}

const matches = (filterValue, itemValue) =>
  toFilterArray(filterValue).includes(itemValue);

// Trafficking_Status as stored, keyed by the label shown in the filter.
const STATUS_BY_LABEL = {
  "Not submitted": "Not Trafficked",
  "Default creative assigned": "Default Ad Assigned",
  Submitted: "Trafficked"
};

export function getLeadTimeDays(startDate, now = new Date()) {
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor((new Date(startDate) - now) / oneDay);
}

export function applyPlacementFilters(placements, placementInfo, filters) {
  const byId = (placementInfo && placementInfo.byId) || {};
  let result = placements || [];

  if (!isAllSelected(filters.status)) {
    const wanted = toFilterArray(filters.status).map(
      (label) => STATUS_BY_LABEL[label]
    );
    result = result.filter((item) => {
      const info = byId[item.id];
      return !!info && wanted.includes(info.Trafficking_Status);
    });
  }

  if (!isAllSelected(filters.site)) {
    result = result.filter((item) => matches(filters.site, item.siteId));
  }

  if (!isAllSelected(filters.start)) {
    const wanted = toFilterArray(filters.start).map((d) => dayjs(d));
    result = result.filter((item) => {
      const info = byId[item.id];
      if (!info) return false;
      return wanted.some((d) => dayjs(info.Start_Date).isSame(d));
    });
  }

  if (!isAllSelected(filters.end)) {
    const wanted = toFilterArray(filters.end).map((d) => dayjs(d));
    result = result.filter((item) => {
      const info = byId[item.id];
      if (!info) return false;
      return wanted.some((d) => dayjs(info.End_Date).isSame(d));
    });
  }

  if (!isAllSelected(filters.leadTime)) {
    result = result.filter((item) =>
      matches(filters.leadTime, getLeadTimeDays(item.pricingSchedule.startDate))
    );
  }

  if (!isAllSelected(filters.lastModified)) {
    result = result.filter((item) =>
      matches(filters.lastModified, item.lastModifiedInfo.time)
    );
  }

  if (!isAllSelected(filters.dimensions)) {
    result = result.filter((item) =>
      matches(filters.dimensions, `${item.size.width} x ${item.size.height}`)
    );
  }

  if (!isAllSelected(filters.compatibility)) {
    result = result.filter((item) =>
      matches(filters.compatibility, item.compatibility)
    );
  }

  if (!isAllSelected(filters.servingType)) {
    result = result.filter((item) => {
      const info = byId[item.id];
      if (!info) return false;
      return matches(filters.servingType, info.Serving_Type || "N/A");
    });
  }

  return [...result];
}
